import { httpClient } from '@bs/core'

// Mirror of legacy frontend/src/pages/Category/PantryCategory/hooks.ts:40-49.
// The Cova classifications endpoint returns one row per parent classification.
export interface RawPOSSubClassification {
  id: string
  name: string
  is_mapped?: boolean
  // Number of products in this sub-classification. Sourced authoritatively from
  // the backend; absent until the Cova view is extended (mapper falls back to 0).
  count?: number
}

export interface RawPOSClassification {
  display_name: string
  sub_classifications: RawPOSSubClassification[]
  // Number of products in this classification. See note on RawPOSSubClassification.count.
  count?: number
}

export interface GetClassificationsPayload {
  store_id: string
  organization_id: string
  company_id: number
}

// Verified against legacy frontend/src/services/apis/cova.ts:89-90 +
// frontend/src/services/apis/category.ts:113-117. The classifications read
// endpoint is still POS-specific (drives the POS tab's category tree) and has
// no unified replacement yet. The write side — formerly
// /cova/pre-onboarding/classifications/update — has been replaced by the
// POS-agnostic PUT /pantry/stores/:storeId/category-mapping in categoryApis.
//
// The Cova read is one of two POS-tree read branches dispatched by the hook
// layer (`usePOSClassifications`). The other branch is Greenline — see
// `getGreenlineClassifications` below, which mirrors legacy
// frontend/src/services/apis/greenline.ts:7-8,60-61.
const COVA_GET_CLASSIFICATIONS = '/cova/pre-onboarding/classifications/'
const GREENLINE_GET_CATEGORY_MAPPING = (storeId: string) =>
  `/greenline/stores/${storeId}/category_mapping`

// The endpoint occasionally responds with an object keyed by classification id
// instead of an array. Legacy normalises both shapes in hooks.ts:344-368.
// We surface the raw payload here and let the hook layer normalise — this
// keeps the gateway shape-honest.
//
// The API does NOT return an aggregate `total_products`; the POS tab derives
// the total client-side by summing each classification's `count` (see
// sumPosTotalProducts in categoryMappers.ts). Any stray non-classification
// sibling key is dropped by the display_name typeguard during normalisation.
export type GetClassificationsResponse =
  | RawPOSClassification[]
  | Record<string, RawPOSClassification>
  | null

// Greenline returns a richer node (compliance category, color hex, image url,
// etc.) but the Category UI only consumes id + name + parent + sub_categories +
// is_mapped. Keep the surface narrow — extra fields are still present at
// runtime but intentionally not typed.
export interface RawGreenlineCategoryNode {
  id: string
  name: string
  parentCategoryId: string | null
  // Greenline company the category belongs to. A Pantry store can link several
  // Greenline companies (`company_ids`); the endpoint merges all their trees.
  companyId?: number | string | null
  sub_categories?: RawGreenlineCategoryNode[]
  is_mapped?: boolean
  isActive?: boolean
  // Number of products in this category. The Greenline category_mapping
  // endpoint returns this per top-level node and per sub_category; absent on
  // older payloads (mapper falls back to 0).
  count?: number
}

// httpClient.json.* unwraps the `{data, status, message}` envelope, so the
// hook layer receives the inner object directly. `woo_predefined_categories`
// is also present in the payload but not used by the Category tabs.
export interface GetGreenlineClassificationsResponse {
  greenline_categories?: Record<string, RawGreenlineCategoryNode>
}

export const posClassificationApis = {
  getClassifications: async (
    payload: GetClassificationsPayload,
  ): Promise<GetClassificationsResponse> => {
    return (await httpClient.json.post(
      COVA_GET_CLASSIFICATIONS,
      payload,
    )) as GetClassificationsResponse
  },

  // GET /greenline/stores/:storeId/category_mapping
  // The `bs-store-id` header is attached by the shared httpClient middleware —
  // no payload needed and no org/company_id requirement (unlike the Cova read).
  getGreenlineClassifications: async (
    storeId: string,
  ): Promise<GetGreenlineClassificationsResponse | null> => {
    return (await httpClient.json.get(
      GREENLINE_GET_CATEGORY_MAPPING(storeId),
    )) as GetGreenlineClassificationsResponse | null
  },
}
